/**
 * Main Application Entry Point -- Product Management Portal
 *
 * Initializes the Vue application with all necessary plugins,
 * stores, and configuration for the standalone product portal.
 */
import { createApp } from 'vue';
import { initializeLogging, initializeTelemetry, createLogger, toTitleCase } from '@tms-phoenix/ui-vue';
import App from './App.vue';
import { router } from './router/instance';
import { routes } from './router';
import { authGuard } from './router/guards';
import { setupPinia, setupVueQuery } from './core/plugins';
import { config } from './core/config';
import { useUIStore, useAuthStore } from './stores';

// Import Tailwind CSS
import './styles/main.css';

// PrimeVue v4 config
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import Aura from '@primeuix/themes/aura';

// Initialize logging first
initializeLogging(config.logging);

// Initialize telemetry
initializeTelemetry(config.telemetry);

const logger = createLogger('main');
logger.info('Initializing Product Management Portal', {
  mode: import.meta.env.MODE,
  version: config.app.version,
});

// Create Vue app instance
const app = createApp(App);

// Setup core plugins
setupPinia(app);
setupVueQuery(app);

// Setup PrimeVue
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: false,
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, primevue',
      },
    },
  },
});
app.use(ToastService);
app.directive('tooltip', Tooltip);
app.config.globalProperties.$titleCase = toTitleCase;

// Initialize UI store after Pinia is set up
const uiStore = useUIStore();
uiStore.initializeFromViewport();

// Re-hydrate auth context and reschedule proactive refresh timer on page reload
const authStore = useAuthStore();
authStore.initRefreshTimer();

// Setup router — add full route table to the singleton (avoids circular deps)
routes.forEach((route) => {
  if (!route.name || !router.hasRoute(route.name)) {
    router.addRoute(route);
  }
});

// Apply navigation guard
// Captured here because vue-router@4 router.onError only receives the error argument.
let pendingNavPath = location.pathname;
router.beforeEach((to, from, next) => {
  pendingNavPath = to.fullPath;
  logger.debug('Navigation start', { from: from.path, to: to.path });
  authGuard(to, from, next);
});

router.afterEach((to, from) => {
  logger.debug('Navigation complete', { from: from.path, to: to.path });
});

router.onError((error) => {
  logger.error('Router error', { error });

  // "Failed to fetch dynamically imported module" / ERR_ABORTED
  // Cause: browser cached old chunk hashes; server now serves new ones after
  // a redeployment. Navigate to the intended destination with fresh chunks.
  // Guard with sessionStorage to avoid an infinite reload loop.
  const isChunkLoadError =
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed') ||
    error?.message?.includes('dynamically imported module');

  if (isChunkLoadError) {
    const reloadKey = `chunk-reload:${pendingNavPath}`;
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1');
      window.location.href = pendingNavPath;
    }
  }
});

// Vite-native preload-error event (Vite ≥ 4.4).
// Fires before the router error for <link rel="modulepreload"> failures.
window.addEventListener('vite:preloadError', () => {
  const reloadKey = `vite-preload-reload:${location.pathname}`;
  if (!sessionStorage.getItem(reloadKey)) {
    sessionStorage.setItem(reloadKey, '1');
    window.location.reload();
  }
});

app.use(router);

// Mount application
app.mount('#app');

logger.info('Product Management Portal mounted successfully');
